import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { OrganizationApi } from 'entities/Organization'
import { type IResponseOrganizationCall } from 'entities/Organization/api/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { openHipaaAddOnModal } from 'pages/settings/ui/HipaaAddOnModal'
import { PhoneCheckrModal } from 'pages/settings/pages/doubleOptIn/ui/PhoneCheckrModal/PhoneCheckrModal'
import { QuietHoursStore } from 'pages/settings/pages/doubleOptIn/ui/QuietHoursSettings/store/QuietHoursStore'

export type BooleanSwitchOption =
  | 'autoCloseConversation'
  | 'autoCloseUnsubscribe'
  | 'globalConversationHistory'
  | 'doubleOptIn'
  | 'extendedInternationalTexting'
  | 'outboundCalls'
  | 'outboundCallsAircall'
  | 'inboundCalls'
  | 'recordAutomatically'
  | 'requireTwoStepAuth'
  | 'phoneCheckr'
  | 'autoAssignContactOwner'
  | 'createNewConvViaAircall'
  | 'hipaaCompliance'
  | 'quietHours'

export type CategoryKey = 'general' | 'conversations' | 'calling' | 'security'

export type NumberSwitchOption = 'autoCloseDays'

export type SwitchOption = BooleanSwitchOption | NumberSwitchOption

type ISwitchHandlerConfig<K extends keyof DoubleOptInPageStore, T> = {
  key: K
  label: string
  api: (value: T, qty?: string) => Promise<unknown>
}

export class DoubleOptInPageStore {
  readonly _quietHoursStore: QuietHoursStore

  public autoCloseConversation = false
  public autoCloseDays = 1
  public autoCloseUnsubscribe = false
  public globalConversationHistory = false
  public doubleOptIn = false
  public extendedInternationalTexting = false
  public outboundCalls = false
  public outboundCallsAircall = false
  public inboundCalls = false
  public recordAutomatically = false
  public requireTwoStepAuth = false
  public phoneCheckr = false
  public autoAssignContactOwner = false
  public createNewConvViaAircall = false
  public hipaaCompliance = false
  public quietHours = false
  private _loader = false
  private _is_hipaa_compliant = false
  private _companyName = ''
  private _savedCompanyName = ''
  private _limitReached = false

  public switchLoadingMap: Record<SwitchOption, boolean> = {
    autoCloseConversation: false,
    autoCloseDays: false,
    autoCloseUnsubscribe: false,
    globalConversationHistory: false,
    doubleOptIn: false,
    extendedInternationalTexting: false,
    outboundCalls: false,
    outboundCallsAircall: false,
    inboundCalls: false,
    recordAutomatically: false,
    requireTwoStepAuth: false,
    phoneCheckr: false,
    autoAssignContactOwner: false,
    createNewConvViaAircall: false,
    hipaaCompliance: false,
    quietHours: false,
  }

  private readonly _categoryKeys: CategoryKey[] = [
    'general',
    'conversations',
    'calling',
    'security',
  ]

  private readonly _categories: Record<CategoryKey, string> = {
    general: 'General',
    conversations: 'Conversations',
    calling: 'Calling',
    security: 'Security',
  }

  private _selectedCategory: CategoryKey = 'general'

  private _initialized = false
  private _organization_call: IResponseOrganizationCall | null = null
  private _booleanSwitchersMap: Record<
    BooleanSwitchOption,
    ISwitchHandlerConfig<BooleanSwitchOption, boolean>
  > = {
    autoCloseConversation: {
      key: 'autoCloseConversation',
      label: 'Auto-close conversations',
      api: (value) =>
        OrganizationApi.updateOrganizationAutoCloseSetting({
          auto_close_setting: value,
        }),
    },
    autoCloseUnsubscribe: {
      key: 'autoCloseUnsubscribe',
      label: 'Auto-close unsubscribe',
      api: (value) =>
        OrganizationApi.updateOrganizationAutoCloseUnsubscribe({
          auto_close_unsubscribe: value,
        }),
    },
    globalConversationHistory: {
      key: 'globalConversationHistory',
      label: 'Global conversations history',
      api: (value) =>
        OrganizationApi.updateOrganizationGlobalConversationHistory({
          use_global_conversation_history: value,
        }),
    },
    doubleOptIn: {
      key: 'doubleOptIn',
      label: 'Double Opt-in',
      api: (value) =>
        OrganizationApi.updateOrganizationDoubleOptIn({
          is_active: value,
        }),
    },
    extendedInternationalTexting: {
      key: 'extendedInternationalTexting',
      label: 'Extended international texting',
      api: async (value) => {
        const response = await OrganizationApi.updateOrganizationExtendedInternationalTexting({
          extended_international_texting: value,
        })

        this._entities.organizationStore.updateExtendedInternationalTexting(value)
        return response
      },
    },
    outboundCalls: {
      key: 'outboundCalls',
      label: 'Outbound calls',
      api: (value) =>
        OrganizationApi.updateOrganizationOutboundInbondCalls({
          is_active: value,
          is_active_inbound: this.inboundCalls,
        }),
    },
    outboundCallsAircall: {
      key: 'outboundCallsAircall',
      label: 'Open outbound calls via Aircall',
      api: (value) =>
        OrganizationApi.updateOrganizationOutboundCallsAircall({
          is_active: value,
        }),
    },
    inboundCalls: {
      key: 'inboundCalls',
      label: 'Inbound calls',
      api: (value) =>
        OrganizationApi.updateOrganizationOutboundInbondCalls({
          is_active: this.outboundCalls,
          is_active_inbound: value,
        }),
    },
    recordAutomatically: {
      key: 'recordAutomatically',
      label: 'Record automatically',
      api: async (value) => {
        const response = await OrganizationApi.updateOrganizationRecordAutomatically({
          is_active: value,
        })

        this._entities.organizationStore.updateOrganizationCallAutoRecord(value)
        return response
      },
    },
    requireTwoStepAuth: {
      key: 'requireTwoStepAuth',
      label: 'Require two-step authentication for your team',
      api: (value) =>
        OrganizationApi.updateOrganizationRequireTwoStepAuth({
          value: value,
        }),
    },
    phoneCheckr: {
      key: 'phoneCheckr',
      label: 'PhoneCheckr',
      api: (value) => {
        if (value) {
          // Placeholder for enable case - actual API call handled separately in handleBooleanSwitcher
          // to support passing checkbox value parameter
          return Promise.resolve()
        } else {
          return OrganizationApi.disableOrganizationPhoneCheckr()
        }
      },
    },
    autoAssignContactOwner: {
      key: 'autoAssignContactOwner',
      label: 'Auto-assign contact owner',
      api: (value) =>
        OrganizationApi.updateAutoAssignContactOwner({
          auto_assign_contact_owner: value,
        }),
    },
    createNewConvViaAircall: {
      key: 'createNewConvViaAircall',
      label: 'Auto-create conversations via Aircall',
      api: (value) =>
        OrganizationApi.updateAutoCreateConversations({
          is_active: value,
        }),
    },
    hipaaCompliance: {
      key: 'hipaaCompliance',
      label: 'hipaaCompliance',
      api: () => {
        runInAction(() => {
          this.hipaaCompliance = this._is_hipaa_compliant
        })
        openHipaaAddOnModal({ entities: this._entities })

        return Promise.resolve()
      },
    },
    quietHours: {
      key: 'quietHours',
      label: 'Quiet Hours',
      api: async (value) => {
        await this.quietHoursStore?.toggleActive(value)
      },
    },
  }

  private _numberSwitchersMap: Record<
    NumberSwitchOption,
    ISwitchHandlerConfig<NumberSwitchOption, number>
  > = {
    autoCloseDays: {
      key: 'autoCloseDays',
      label: 'Auto-close days',
      api: (_value: number, qty = '1') =>
        OrganizationApi.updateOrganizationAutoCloseDays({
          auto_close_days: qty,
        }),
    },
  }

  constructor(private _entities: IEntitiesStore) {
    this._quietHoursStore = new QuietHoursStore(this._entities, (isActive: boolean) => {
      runInAction(() => {
        this.quietHours = isActive
      })
    })

    makeAutoObservable(this)
    this._init()

    reaction(
      () => this._entities.organizationStore.organization_call,
      (organizationCall) => {
        if (this._initialized && !this.hasAnyCallSettingsLoading) {
          this._updateCallSettings(organizationCall)
        }
      }
    )
  }
  private async _init() {
    await Promise.all([
      this._entities.organizationStore.initPromise,
      this._entities.usersStore.initPromise,
    ])

    runInAction(() => {
      this._companyName = this._savedCompanyName = this._entities.organizationStore.name || ''
      this.autoCloseConversation = this._entities.organizationStore.autoCloseConversation ?? false
      this.autoCloseDays = Number(this._entities.organizationStore.autoCloseDays ?? 1)
      this.autoCloseUnsubscribe = this._entities.organizationStore.autoCloseUnsubscribe ?? false
      this.globalConversationHistory = this._entities.organizationStore.globalConversationHistory
      this.doubleOptIn = this._entities.organizationStore.doubleOptInEnabled
      this.extendedInternationalTexting =
        this._entities.organizationStore.extendedInternationalTexting
      this.requireTwoStepAuth = this._entities.organizationStore.isTwoFaEnabled
      this.phoneCheckr = this._entities.organizationStore.isPhoneCheckerEnabled
      this.autoAssignContactOwner = this._entities.organizationStore.autoAssignContactOwner
      this.createNewConvViaAircall = this._entities.organizationStore.createNewConvViaAircall
      this.hipaaCompliance = this._is_hipaa_compliant =
        this._entities.organizationStore.isHipaaCompliant
      this._updateCallSettingsSync(this._entities.organizationStore.organization_call)
      this.quietHours = this._entities.organizationStore.quietHoursSettings?.is_active ?? false
      this._quietHoursStore.init()
      this._initialized = true
    })
  }

  private _updateCallSettingsSync(organizationCall: IResponseOrganizationCall | null) {
    this._organization_call = organizationCall
    this.outboundCalls = organizationCall?.is_active ?? false
    this.inboundCalls = organizationCall?.is_active_inbound ?? false

    if (
      organizationCall?.call_via_aircall !== undefined &&
      organizationCall?.call_via_aircall !== null
    ) {
      this.outboundCallsAircall = organizationCall.call_via_aircall.is_active ?? false
    }

    if (
      organizationCall?.call_auto_record !== undefined &&
      organizationCall?.call_auto_record !== null
    ) {
      this.recordAutomatically = organizationCall.call_auto_record.is_active ?? false
    }
  }

  private _updateCallSettings(organizationCall: IResponseOrganizationCall | null) {
    runInAction(() => {
      this._updateCallSettingsSync(organizationCall)
    })
  }

  updateCompanyName = async (value: string) => {
    try {
      if (this._loader || this.limitReached) return

      const trimmedValue = value.trim()
      this.setCompanyName(trimmedValue)

      if (!trimmedValue || trimmedValue === this._savedCompanyName) return

      runInAction(() => {
        this._loader = true
      })

      await OrganizationApi.updateOrganizationName({
        name: trimmedValue,
      })

      runInAction(() => {
        this._savedCompanyName = trimmedValue
        this._entities.organizationStore.updateOrganizationName(trimmedValue)
      })

      showToast({
        type: 'success',
        title: 'Company name updated',
      })

      this._entities.eventLogStore.logEvent(
        'Company Name Updated',
        {
          event_id: 'company_name_updated',
          userId: this._entities.usersStore.user_owner_id || 'anonymous_user',
          email: this._entities.usersStore.user?.email || '',
          agencyId: this._entities.organizationStore.agency_id,
        },
        { groupId: this._entities.organizationStore.id }
      )
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError && e.errors.getError('name')) {
        this.showErrorMessage(e.errors.getError('name'))
      } else {
        this.showErrorMessage()
      }

      const initialOrganizationName = this._entities.organizationStore.name
      if (initialOrganizationName) {
        runInAction(() => {
          this._companyName = initialOrganizationName
        })
      }
    } finally {
      runInAction(() => {
        this._loader = false
      })
    }
  }

  public async handleBooleanSwitcher(option: BooleanSwitchOption, value: boolean) {
    const config = this._booleanSwitchersMap[option]

    runInAction(() => {
      this.switchLoadingMap[option] = true
      this[config.key] = value
    })

    try {
      if (option === 'doubleOptIn' && value) {
        const confirmed = await this.confirmDoubleOptIn()
        if (!confirmed) {
          runInAction(() => {
            this[option] = !value
            this.switchLoadingMap[option] = false
          })
          return
        }
      }

      if (option === 'doubleOptIn') {
        this._entities.organizationStore.updateDoubleOptIn(value)
      }

      if (option === 'phoneCheckr' && value) {
        const result = await this.confirmPhoneCheckr()
        if (!result.confirmed) {
          runInAction(() => {
            this[option] = !value
            this.switchLoadingMap[option] = false
          })
          return
        }

        // Handle phoneCheckr enable separately to pass checkbox value
        // Cannot use generic api() due to different parameter signature
        await OrganizationApi.enableOrganizationPhoneCheckr({
          check_existing_contacts: result.checked,
        })
      } else {
        await config.api(value)
      }

      if (option !== 'hipaaCompliance' && option !== 'quietHours') {
        showToast({
          type: 'success',
          title: `${config.label} ${value ? 'enabled' : 'disabled'}`,
        })
      }
    } catch (e) {
      logger.error(e)
      runInAction(() => {
        this[option] = !value
      })
      this.showErrorMessage()
    } finally {
      runInAction(() => {
        this.switchLoadingMap[option] = false
      })
    }
  }

  public async handleNumberSwitcher(option: NumberSwitchOption, qty: string) {
    const config = this._numberSwitchersMap[option]
    const parsedQty = Number(qty)

    if (isNaN(parsedQty)) {
      showToast({ type: 'error', title: 'Invalid number format' })
      return
    }

    runInAction(() => {
      this.switchLoadingMap[option] = true
      this[option] = parsedQty
    })

    try {
      await config.api(parsedQty, qty)
      showToast({ type: 'success', title: `${config.label} saved` })
    } catch (e) {
      logger.error(e)
      this.showErrorMessage()
    } finally {
      runInAction(() => {
        this.switchLoadingMap[option] = false
      })
    }
  }

  private confirmDoubleOptIn(): Promise<boolean> {
    return new Promise((resolve) => {
      const modalId = nanoid()
      const handleCancel = () => {
        modalStore.removeModal(modalId)
        resolve(false)
      }

      modalStore.addModal({
        id: modalId,
        title: 'Enable Double Opt-in?',
        desc: [
          'Turning this on sends a templated opt-in message to every new contact. Only confirmed contacts can be messaged. This applies to your whole organization.',
        ],
        type: ModalTypeList.INFO,
        onClose: handleCancel,
        primaryAction: {
          text: 'Enable Double Opt-in',
          onAction: () => {
            modalStore.removeModal(modalId)
            resolve(true)
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: handleCancel,
        },
      })
    })
  }

  private confirmPhoneCheckr(): Promise<{ confirmed: boolean; checked: boolean }> {
    return new Promise((resolve) => {
      const modalId = nanoid()
      const handleCancel = () => {
        modalStore.removeModal(modalId)
        resolve({ confirmed: false, checked: false })
      }

      const handleConfirm = (value: boolean) => {
        modalStore.removeModal(modalId)
        resolve({ confirmed: true, checked: value })
      }

      modalStore.addModal({
        id: modalId,
        width: 480,
        title: 'Enable PhoneCheckr',
        type: ModalTypeList.DEFAULT,
        ModalContent: PhoneCheckrModal,
        ModalContentProps: {
          onCancel: handleCancel,
          onConfirm: handleConfirm,
        },
        paddingContent: '0 24px 24px',
        onClose: handleCancel,
        showCloseButton: false,
      })
    })
  }

  showErrorMessage(message?: string) {
    showToast({
      type: 'error',
      title: message ? message : 'Something went wrong. Please try again',
    })
  }

  setCompanyName = (value: string) => {
    this._companyName = value
  }

  get limitReached() {
    return this._limitReached
  }

  setLimitReached = (status: boolean) => {
    this._limitReached = status
  }

  get companyName() {
    return this._companyName
  }

  get isStoreInitialized() {
    return this._initialized
  }

  get hasAnyCallSettingsLoading() {
    return (
      this.switchLoadingMap.outboundCalls ||
      this.switchLoadingMap.outboundCallsAircall ||
      this.switchLoadingMap.inboundCalls ||
      this.switchLoadingMap.recordAutomatically
    )
  }

  get isHipaaCompliant() {
    return this._is_hipaa_compliant
  }

  get isHipaaSettingVisible() {
    return !!this._entities.usersStore.user?.isAdminOrOwnerRole
  }

  get disabledTwoStepAuth() {
    return this._is_hipaa_compliant && this.requireTwoStepAuth
  }

  get quietHoursStore() {
    return this._quietHoursStore
  }

  get selectedCategory() {
    return this._selectedCategory
  }

  setActiveCategory = (categoryKey: CategoryKey) => {
    this._selectedCategory = categoryKey
  }

  setActiveCategoryFromParam = (param: string | null) => {
    const category = this._categoryKeys.find((key) => key === param)

    if (category) {
      this.setActiveCategory(category)
    }
  }

  handleCategorySwitch = (categoryKey: CategoryKey) => {
    if (categoryKey === this._selectedCategory) return

    const hasUnsavedQuietHours =
      this._selectedCategory === 'general' && this._quietHoursStore.isShowActions

    if (hasUnsavedQuietHours) {
      this._quietHoursStore.openAskIfSaveModal({
        onToggle: () => {},
        onProceed: () => this.setActiveCategory(categoryKey),
      })
    } else {
      this.setActiveCategory(categoryKey)
    }
  }

  get categories(): {
    key: CategoryKey
    label: string
  }[] {
    return this._categoryKeys.map((key) => ({
      key,
      label: this._categories[key],
    }))
  }

  get isSelectedCategoryGeneral() {
    return this._selectedCategory === 'general'
  }

  get isSelectedCategoryConversations() {
    return this._selectedCategory === 'conversations'
  }

  get isSelectedCategoryCalling() {
    return this._selectedCategory === 'calling'
  }

  get isSelectedCategorySecurity() {
    return this._selectedCategory === 'security'
  }
}
